import { useEffect, useRef, useState } from 'react'
import { DEFAULT_OVERVIEW_LAYOUT, OVERVIEW_LAYOUT_KEY, type OverviewLayout, parseOverviewLayout } from '../lib/overviewStatus'
import { ActionIcon } from './WorkspaceActions'
import { serverStorage } from '../lib/userDocuments'

export function useOverviewLayout() {
  const [layout, setLayout] = useState<OverviewLayout>(() => {
    // Storage this browser refuses reads as a new browser: the complete overview.
    try { return parseOverviewLayout(serverStorage.getItem(OVERVIEW_LAYOUT_KEY)) } catch { return { ...DEFAULT_OVERVIEW_LAYOUT } }
  })
  const save = (next: OverviewLayout) => {
    try { serverStorage.setItem(OVERVIEW_LAYOUT_KEY, JSON.stringify(next)); setLayout(next); return '' }
    catch { return 'Your layout could not be saved. Reload the page and try again.' }
  }
  return { layout, save }
}

export function OverviewCustomizer({ value, onSave }: { value: OverviewLayout; onSave: (value: OverviewLayout) => string }) {
  const [open, setOpen] = useState(false)
  const trigger = useRef<HTMLButtonElement>(null)
  return <>
    <button type="button" ref={trigger} className="button ov-customize-trigger" onClick={() => setOpen(true)} aria-label="Customize overview"><ActionIcon kind="layout" /><span>Customize</span></button>
    {open && <LayoutDialog value={value} onSave={onSave} onClose={() => { setOpen(false); trigger.current?.focus() }} />}
  </>
}

/** The rows name the sections as their headings on the page do, so a reader
 *  can tell which switch hides which. Filters are not hidden with a
 *  section: the search and project filter stay above whatever is shown. */
const ROWS: { key: keyof OverviewLayout; title: string; detail: string }[] = [
  { key: 'summary', title: 'Summary Cards', detail: 'System status, alarms and issue counts' },
  { key: 'attention', title: 'Needs Attention', detail: 'Issues to review, with your flags' },
  { key: 'alarms', title: 'Alarms', detail: 'Open alarms by station or channel' },
  { key: 'registry', title: 'Stations', detail: 'Every station with its status and last observation' },
]

function LayoutDialog({ value, onSave, onClose }: { value: OverviewLayout; onSave: (value: OverviewLayout) => string; onClose: () => void }) {
  const dialog = useRef<HTMLDialogElement>(null)
  const [draft, setDraft] = useState({ ...value })
  const [error, setError] = useState('')
  useEffect(() => {
    const focus = document.activeElement instanceof HTMLElement ? document.activeElement : null
    const overflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'; dialog.current?.showModal()
    return () => { dialog.current?.close(); document.body.style.overflow = overflow; focus?.focus() }
  }, [])
  return <dialog ref={dialog} className="ov-customizer" aria-labelledby="ov-customizer-title" onCancel={event => { event.preventDefault(); onClose() }}>
    <header><h2 id="ov-customizer-title">Customize Overview</h2><button type="button" onClick={onClose} aria-label="Close overview settings"><ActionIcon kind="close" /></button></header>
    <div className="ov-customizer-body">{ROWS.map(row => <label key={row.key} className="ov-customizer-row"><span>{row.title}<small>{row.detail}</small></span>
      <input type="checkbox" checked={draft[row.key]} onChange={event => setDraft(current => ({ ...current, [row.key]: event.target.checked }))} /></label>)}
      <p>Layout saved to your account.</p>{error && <p className="ov-customizer-error" role="alert">{error}</p>}
    </div>
    <footer><button className="button" type="button" onClick={() => { setDraft({ ...DEFAULT_OVERVIEW_LAYOUT }); setError('') }}>Reset</button>
      <button className="button" type="button" onClick={onClose}>Cancel</button><button className="button primary" type="button" onClick={() => { const failure = onSave(draft); if (failure) setError(failure); else onClose() }}>Apply layout</button></footer>
  </dialog>
}
